import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost } from '@/lib/api';

export interface GoogleIntegrationStatus {
  connected: boolean;
  configured: boolean;
  google_user_email: string | null;
  ga4_property_id: string | null;
  gsc_site_url: string | null;
  scopes: string[] | null;
  connected_at: string | null;
  updated_at: string | null;
}

export interface Ga4PropertyOption {
  id: string;
  name: string;
  account: string;
}

export interface GscSiteOption {
  site_url: string;
  permission_level: string;
}

interface PropertiesResponse {
  ga4_properties: Ga4PropertyOption[];
  gsc_sites: GscSiteOption[];
}

export function useGoogleIntegrationStatus(projectId: string | undefined) {
  return useQuery({
    queryKey: ['google-integration', projectId],
    queryFn: () =>
      apiGet<GoogleIntegrationStatus>(`/v1/projects/${projectId}/integrations/google`),
    enabled: !!projectId,
  });
}

export function useGoogleProperties(projectId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['google-integration', projectId, 'properties'],
    queryFn: () =>
      apiGet<PropertiesResponse>(`/v1/projects/${projectId}/integrations/google/properties`),
    enabled: !!projectId && enabled,
    staleTime: 60_000,
  });
}

export function useSaveGoogleProperties(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { ga4_property_id: string | null; gsc_site_url: string | null }) =>
      apiPost<void>(`/v1/projects/${projectId}/integrations/google/properties`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['google-integration', projectId] });
    },
  });
}

export function useDisconnectGoogle(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete<void>(`/v1/projects/${projectId}/integrations/google`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['google-integration', projectId] });
    },
  });
}
